import { HTMLProps, useEffect, useRef, useState } from "react";

import { DateTime } from "luxon";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { createColumnHelper, RowModel } from "@tanstack/react-table";

import { IPart, IPartCategory } from "../../../models/Part";
import formatDateTime from "../../../utils/formatDateTime";
import openInNewTab from "../../../utils/openInNewTab";
import PartStatusLabel from "../../ebayOrders/PartStatusLabel";
import ClickToCopy from "../../shared/ClickToCopy";
import PagedTable from "../../shared/Tables/PagedTable";
import PartImage from "../../stock/PartImage";
import UnlistedOfferPrice from "../unlistedTable/UnlistedOfferPrice";
import { BatchItemType, IEbayListingBatchItem } from "../../../models/bulkListings/BulkListing";
import { ListingStatus } from "../../../pages/EbayListings/ViewListing";
import RecentlyListedErrorCell from "./RecentlyListedErrorCell";
import usePartDefaultCategoriesMutations from "../../../data/usePartDefaultCategoriesMutations";
import BulkListModal from "../unlistedTable/BulkListModal";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../notifications/useNotifications";
import { CheckboxInput } from "../../shared/Inputs/CheckboxInput";
import { PageMode } from "../../../models/PagedTable";


function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}


export default function RecentlyListedTable({ categories }: { categories: IPartCategory[] | undefined; }) {
  const { useBulkDefaultPartCategory } = usePartDefaultCategoriesMutations();
  const { saveHandlers: bulkSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const { addNotification } = useNotification();

  const [typeFilter, setTypeFilter] = useState<BatchItemType | null>(null);

  const [bulkListModalOpen, setBulkListModalOpen] = useState<boolean>(false);
  const [selectedPartsForBulkList, setSelectedPartsForBulkList] = useState<IPart[] | undefined>(undefined);

  const colHelper = createColumnHelper<IEbayListingBatchItem>();

  const cols = [
    {
      id: 'select',
      header: ({ table }: any) => (
        <IndeterminateCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: any) => (
        <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
          <IndeterminateCheckbox
            {...{
              id: row.original.id,
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
          <button
            onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
            style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
            className="ml-3 p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
          >
            {/* {row.getIsExpanded() ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4"/>} */}
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
    colHelper.display({
      id: "images",
      cell: (info) => <PartImage part={info.row.original.partData} width="min-w-10 max-w-32" />
    }),
    colHelper.accessor("partData.offers", {
      header: "Listing Title",
      id: "listingTitle",
      sortDescFirst: false,
      sortingFn: "auto",
      cell: (info) => <span>{info.getValue()?.at(0)?.title ?? "N/A"}</span>,
    }),
    colHelper.accessor("partData.categoryId", {
      header: "Category",
      id: "categoryId",
      sortDescFirst: false,
      sortingFn: "auto",
      cell: (info) => <span>{info.getValue() ? categories?.find((cat) => cat.id === info.getValue())?.name : "N/A"}</span>,
    }),
    colHelper.accessor("partData.tagNumber", {
      header: "Tag No.",
      id: "tagNumber",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col">
        <ClickToCopy text={info.row.original.partData.tagNumber} />
      </div>,
    }),
    colHelper.accessor("partData.vehicle.vrm", {
      header: "Vehicle",
      id: "vrm",
      sortingFn: "auto",
      cell: (info) => <div className="flex flex-col">
        {/* <span>{info.row.original.vehicle?.vrm ?? ""}</span> */}
        <ClickToCopy text={info.row.original.partData.vehicle?.vrm} />
        <span>{info.row.original.partData.vehicle?.combinedMake ?? ""}</span>
        <span>{info.row.original.partData.vehicle?.combinedModel ?? ""}</span>
        <ClickToCopy text={info.row.original.partData.vehicle?.combinedVIN} textClassName="text-xs" />
      </div>,
    }),
    colHelper.accessor(row => {
      const offer = row.partData.offers[0];
      return offer ? ((offer.auctionStartPrice ?? 0) + (offer.buyItNowPrice ?? 0)) : 0;
    }, {
      id: "offer price",
      header: "Price",
      sortingFn: (rowA: any, rowB: any, columnId: any) => {
        //Sort by value of both
        return (parseFloat(rowA.original.offers[0]?.auctionStartPrice ?? 0)) + (parseFloat(rowA.original.offers[0]?.buyItNowPrice ?? 0)) -
          (parseFloat(rowB.original.offers[0]?.auctionStartPrice ?? 0)) - (parseFloat(rowB.original.offers[0]?.buyItNowPrice ?? 0));
      },
      cell: (info) => <UnlistedOfferPrice part={info.row.original.partData} />
    }),
    colHelper.accessor("created", {
      id: "created",
      header: "Listed On",
      cell: (info) => (
        <div className="flex flex-col items-center justify-center">
          <p>{formatDateTime(info.getValue(), DateTime.TIME_SIMPLE)}</p>
          <p>{formatDateTime(info.getValue(), DateTime.DATE_SHORT)}</p>
        </div>
      ),
    }),
    colHelper.accessor("partData.partStatus", {
      header: "Part Status",
      enableSorting: false,
      cell: (info) => <PartStatusLabel part={info.row.original.partData} />,
    }),
    colHelper.accessor("partData.offers", {
      id: "offer status",
      header: "Offer Status",
      enableSorting: false,
      cell: (info) => <ListingStatus offer={info.getValue()?.at(0)} part={info.row.original.partData} />,
    }),
    colHelper.display({
      header: "Listed Status",
      enableSorting: false,
      cell: (info) => <RecentlyListedErrorCell batchItem={info.row.original} />,
    }),
  ];

  const bulkList = (rows: RowModel<IEbayListingBatchItem>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected() && row.original.isListed === false);

    if (selected.length === 0) {
      addNotification({
        primaryText: "No unlisted parts selected",
        variant: "warn"
      });
      return;
    }

    setSelectedPartsForBulkList(rows.rows.map((row) => row.original.partData));
    setBulkListModalOpen(true);
  };

  const handleApplyBulkDefaults = (rows: RowModel<IEbayListingBatchItem>) => {
    const selected = rows.rows.filter((row) => row.getIsSelected() && row.original.isListed === false);

    if (selected.length === 0) {
      addNotification({
        primaryText: "No unlisted parts selected",
        variant: "warn"
      });
      return;
    }

    const body = {
      partIds: selected.map((row) => row.original.partDataId)
    };

    useBulkDefaultPartCategory.mutate(body as any, bulkSaveHandlers);
  };

  return (
    <>
      {selectedPartsForBulkList && (
        <BulkListModal
          open={bulkListModalOpen}
          setOpen={setBulkListModalOpen}
          selectedParts={selectedPartsForBulkList}
        />
      )}

      <PagedTable
        url="/bulklisting/items"
        pageMode={PageMode.RecentlyListed}
        columns={cols}
        queryLabel="Search part details"
        customUrlQuery=""
        onRowClick={(x: IEbayListingBatchItem) => openInNewTab(`ebay-listings/${x.partData.id}`)}
        actionButtons={[
          { label: "Bulk List", onClick: bulkList, isLoading: false, disabledOnNoRows: true },
          { label: "Apply Defaults", onClick: handleApplyBulkDefaults, isLoading: useBulkDefaultPartCategory.isLoading, disabledOnNoRows: true },
        ]}
        showTotal
        modeToggles={[
          { label: "Only Show Failed", pageMode: PageMode.RecentlyListedFailed },
        ]}
      />
    </>
  )
}